import { type PropsWithChildren, useEffect } from 'react';

import { toQuotedString } from '@atlassian/jira-common-jql/src/helpers.tsx';
import type { LinkResolverOptions } from '../controllers/features-context/index.tsx';

/**
 * Generates a URL for the list view with query params based on the provided options.
 *
 * @param {LinkResolverOptions} options - The options to generate the list view URL.
 * @param {string} [options.sortBy] - The field to sort by.
 * @param {boolean} [options.isDescending] - ascending by default; Appended only when sortBy is provided.
 * @param {string} [options.filter] - The filter to apply to the list view; Escaping is handled out-of-the-box.
 * @param {boolean} [options.hideDone]
 * @returns {string} The generated list view URL.
 */
export const generateListViewParams = (options: LinkResolverOptions) => {
	const params = new URLSearchParams();

	if (options.sortBy) {
		params.append('sortBy', options.sortBy);
		params.append('direction', options.isDescending ? 'DESC' : 'ASC');
	}

	if (options.filter) {
		// URLSearchParams provides escaping, except for single quotes which should
		// utilise the escapeFilterChars function below
		params.append('filter', options.filter);
	}

	if (typeof options.hideDone === 'boolean') {
		params.append('hideDone', options.hideDone ? 'true' : 'false');
	}

	if (options.openCreateIssueDialog) {
		params.append('inlineCreate', 'true');
	}

	return `${params.toString()}`;
};

export const generateJqlString = (projectKey: string, options: LinkResolverOptions) => {
	let jqlString = `project = ${toQuotedString(projectKey)}`;

	if (options.filter) {
		jqlString += ` AND ${options.filter}`;
	}

	if (options.hideDone) {
		jqlString += ' AND statusCategory != Done';
	}

	if (options.sortBy) {
		const direction = options.isDescending ? 'DESC' : 'ASC';
		jqlString += ` ORDER BY ${options.sortBy} ${direction}`;
	}

	return jqlString;
};

// to be used in conjunction with LinkResolverOptions's filter attribute where
// the filter string is wrapped in single quotes and needs to be escaped if the
// filter string contains single quotes
export const escapeFilterChars = (filterString: string) => {
	return filterString.replace(/'/g, "\\'");
};

export const RenderSuccess = ({
	onSuccess,
	children,
}: PropsWithChildren<{ onSuccess: () => void }>) => {
	useEffect(() => {
		onSuccess();
	}, [onSuccess]);
	return children;
};
